import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, catchError, throwError } from 'rxjs';
import { environment } from 'environments/environment';
import { AuthUtils } from 'app/core/auth/utils/auth.utils';
import { AUTH_ENDPOINTS, AuthService } from '../auth/services/auth.service';

const TOKEN_EXPIRY_OFFSET_SECONDS = 60;

const SIGN_IN_ENDPOINTS = [AUTH_ENDPOINTS.LOGIN, AUTH_ENDPOINTS.LOGIN_ADMIN];

export const authInterceptor = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const _authService = inject(AuthService);

  // Whether the 401 belongs to us, not whether a token went out with the request. A token that
  // expired while the tab sat open is skipped below and still earns a 401 from our own API — that
  // is the most common way a session ends, and gating the redirect on "we attached something"
  // left exactly that case stranded on a dead page.
  const isApiRequest = targetsPortalApi(req);

  let nextRequest = req;

  if (isApiRequest &&
      _authService.accessToken &&
      !AuthUtils.isTokenExpired(_authService.accessToken, TOKEN_EXPIRY_OFFSET_SECONDS)) {
    nextRequest = req.clone({
      setHeaders: {
        Authorization: `Bearer ${_authService.accessToken}`
      }
    });
  }

  // A 401 from the sign-in call means the credentials were wrong, not that a session ended.
  // Redirecting there would reload the page out from under the message explaining why.
  const endsSessionOn401 = isApiRequest && !isSignInRequest(req);

  return next(nextRequest).pipe(
    catchError((error: unknown) => {
      if (endsSessionOn401 && error instanceof HttpErrorResponse && error.status === 401) {
        const signInUrl = _authService.signInUrlForEndedSession(
          `${window.location.pathname}${window.location.search}`
        );

        _authService.signOut();
        authRedirect.toSignIn(signInUrl);
      }

      return throwError(() => error);
    })
  );
};

function isSignInRequest(req: HttpRequest<unknown>): boolean {
  const url = req.url.toLowerCase();

  return SIGN_IN_ENDPOINTS.some((endpoint) => url.includes(endpoint.toLowerCase()));
}

function targetsPortalApi(req: HttpRequest<unknown>): boolean {
  const apiOrigins = [
    environment.webServiceUrl,
  ]
    .filter((url): url is string => !!url)
    .map((url) => normalizeUrl(url));

  const requestUrl = normalizeUrl(req.url);

  return apiOrigins.some((apiUrl) => requestUrl.startsWith(apiUrl));
}

function normalizeUrl(url: string): string {
  try {
    return new URL(url, window.location.origin).toString().replace(/\/$/, '');
  } catch {
    return url.replace(/\/$/, '');
  }
}

/**
 * Held on an object rather than called as a bare function so a spec can replace it. A browser will
 * not let `window.location.assign` be spied on, and letting the real one run would navigate the
 * test runner's own page out from under the remaining specs.
 */
export const authRedirect = {
  toSignIn: (url: string): void => window.location.assign(url)
};
